<template>
  <div class="wc-dashboard">
    <!-- KPI Cards -->
    <v-row dense>
      <v-col cols="6" sm="6" md="3">
        <div class="kpi-card kpi-1">
          <div class="kpi-label">Termine</div>
          <div class="kpi-value"><number :from="0" :to="totals.totalAppointments" :duration="0.8" /></div>
          <div class="kpi-sub">Ø {{ totals.avgAppointmentsPerDay }} / Tag</div>
        </div>
      </v-col>
      <v-col cols="6" sm="6" md="3">
        <div class="kpi-card kpi-2">
          <div class="kpi-label">No-Show Rate</div>
          <div class="kpi-value">{{ totals.noShowRate }}%</div>
          <div class="kpi-sub">{{ (totals.statusDistribution || {}).noshow || 0 }} Termine</div>
        </div>
      </v-col>
      <v-col cols="6" sm="6" md="3">
        <div class="kpi-card kpi-3">
          <div class="kpi-label">Task-Quote</div>
          <div class="kpi-value">{{ totals.taskCompletionRate }}%</div>
          <div class="kpi-sub">{{ totals.tasksDone }} / {{ totals.totalTasks }} erledigt</div>
        </div>
      </v-col>
      <v-col cols="6" sm="6" md="3">
        <div class="kpi-card kpi-4">
          <div class="kpi-label">Schichten</div>
          <div class="kpi-value"><number :from="0" :to="totals.totalShifts" :duration="0.8" /></div>
          <div class="kpi-sub">{{ totals.totalEmployees }} Mitarbeiter</div>
        </div>
      </v-col>
    </v-row>

    <v-row dense class="mt-2">
      <v-col cols="6" sm="6" md="3">
        <div class="kpi-card kpi-5">
          <div class="kpi-label">Aufgaben</div>
          <div class="kpi-value"><number :from="0" :to="totals.totalTasks" :duration="0.8" /></div>
          <div class="kpi-sub">{{ totals.completionsCount }} Ämtli-Checks</div>
        </div>
      </v-col>
      <v-col cols="6" sm="6" md="3">
        <div class="kpi-card kpi-6">
          <div class="kpi-label">Gelöscht</div>
          <div class="kpi-value"><number :from="0" :to="totals.deletedCount" :duration="0.8" /></div>
          <div class="kpi-sub">Termine entfernt</div>
        </div>
      </v-col>
      <v-col cols="6" sm="6" md="3">
        <div class="kpi-card kpi-7">
          <div class="kpi-label">Check-Ins</div>
          <div class="kpi-value"><number :from="0" :to="(totals.statusDistribution || {}).checkedin || 0" :duration="0.8" /></div>
          <div class="kpi-sub">Erschienen</div>
        </div>
      </v-col>
      <v-col cols="6" sm="6" md="3">
        <div class="kpi-card kpi-8">
          <div class="kpi-label">Absagen</div>
          <div class="kpi-value"><number :from="0" :to="(totals.statusDistribution || {}).cancelled || 0" :duration="0.8" /></div>
          <div class="kpi-sub">Storniert</div>
        </div>
      </v-col>
    </v-row>

    <!-- Tabs -->
    <v-card class="mt-4 glass-card" flat>
      <v-tabs v-model="activeTab" background-color="transparent" dark grow show-arrows>
        <v-tab>Übersicht</v-tab>
        <v-tab>Mitarbeiter</v-tab>
        <v-tab>Ämtli & Tasks</v-tab>
        <v-tab>Heatmap</v-tab>
        <v-tab>Wochentag</v-tab>
        <v-tab>Löschungen</v-tab>
        <v-tab>Rohdaten</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab" style="background: transparent">
        <!-- TAB 1: ÜBERSICHT -->
        <v-tab-item>
          <div class="pa-4">
            <v-row>
              <v-col cols="12" md="6">
                <div class="chart-card">
                  <div class="chart-title">Termine & Aufgaben pro Tag</div>
                  <apexchart type="area" height="320" :options="trendOpts" :series="trendSeries" />
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="chart-card">
                  <div class="chart-title">Termin-Status-Verteilung</div>
                  <apexchart type="donut" height="320" :options="donutOpts" :series="donutSeries" />
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="chart-card">
                  <div class="chart-title">No-Shows & Absagen (Trend)</div>
                  <apexchart type="line" height="320" :options="noShowTrendOpts" :series="noShowTrendSeries" />
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="chart-card">
                  <div class="chart-title">Stündliche Verteilung (alle Tage)</div>
                  <apexchart type="bar" height="320" :options="hourlyBarOpts" :series="hourlyBarSeries" />
                </div>
              </v-col>
            </v-row>
          </div>
        </v-tab-item>
        <!-- TAB 2: MITARBEITER -->
        <v-tab-item>
          <div class="pa-4">
            <v-row>
              <v-col cols="12" md="7">
                <div class="chart-card">
                  <div class="chart-title">Termine pro Mitarbeiter</div>
                  <apexchart type="bar" height="360" :options="empBarOpts" :series="empBarSeries" />
                </div>
              </v-col>
              <v-col cols="12" md="5">
                <div class="chart-card">
                  <div class="chart-title">Top 5 Performer</div>
                  <div v-for="(e, i) in topEmployees" :key="e.id" class="top-slot-row">
                    <div class="rank">#{{ i + 1 }}</div>
                    <div class="flex-grow-1">
                      <div class="slot-title">{{ e.name }}</div>
                      <div class="slot-sub">{{ e.shifts }} Schichten · {{ e.totalWorkHours }}h</div>
                    </div>
                    <div class="text-right">
                      <div class="slot-value">{{ e.appointments }}</div>
                      <div class="slot-sub">{{ e.noShowRate }}% No-Show</div>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
            <v-data-table
              dark
              dense
              class="mt-4 transparent-table"
              :headers="empHeaders"
              :items="data ? data.employeeStats : []"
              :items-per-page="20"
              sort-by="appointments"
              sort-desc>
              <template v-slot:item.taskCompletionRate="{ item }"
                ><v-chip x-small :color="rateColor(item.taskCompletionRate)" dark>{{ item.taskCompletionRate }}%</v-chip></template
              >
              <template v-slot:item.noShowRate="{ item }"
                ><v-chip x-small :color="noShowColor(item.noShowRate)" dark>{{ item.noShowRate }}%</v-chip></template
              >
            </v-data-table>
          </div>
        </v-tab-item>
        <!-- TAB 3: ÄMTLI & TASKS -->
        <v-tab-item>
          <div class="pa-4">
            <v-row>
              <v-col cols="12" md="6">
                <div class="chart-card">
                  <div class="chart-title">Erledigungsrate pro Aufgabentyp</div>
                  <apexchart type="bar" height="320" :options="tplBarOpts" :series="tplBarSeries" />
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="chart-card">
                  <div class="chart-title">Tasks über Zeit</div>
                  <apexchart type="area" height="320" :options="taskTrendOpts" :series="taskTrendSeries" />
                </div>
              </v-col>
            </v-row>
            <v-data-table
              dark
              dense
              class="mt-4 transparent-table"
              :headers="tplHeaders"
              :items="data ? data.templateStats : []"
              :items-per-page="20"
              sort-by="usageCount"
              sort-desc>
              <template v-slot:item.completionRate="{ item }"
                ><v-chip x-small :color="rateColor(item.completionRate)" dark>{{ item.completionRate }}%</v-chip></template
              >
              <template v-slot:item.category="{ item }"
                ><v-chip x-small dark>{{ item.category }}</v-chip></template
              >
            </v-data-table>
          </div>
        </v-tab-item>
        <!-- TAB 4: HEATMAP -->
        <v-tab-item>
          <div class="pa-4">
            <div class="d-flex align-center mb-3">
              <v-select
                v-model="heatmapMetric"
                :items="[
                  { text: 'Termine', value: 'appointments' },
                  { text: 'Tasks', value: 'tasks' },
                ]"
                dark
                hide-details
                dense
                outlined
                style="max-width: 240px" />
            </div>
            <div class="chart-card">
              <apexchart type="heatmap" height="420" :options="hmOpts" :series="hmSeries" />
            </div>
          </div>
        </v-tab-item>
        <!-- TAB 5: WOCHENTAG -->
        <v-tab-item>
          <div class="pa-4">
            <v-row>
              <v-col cols="12" md="6">
                <div class="chart-card">
                  <div class="chart-title">Ø Termine pro Wochentag</div>
                  <apexchart type="bar" height="300" :options="wdBarOpts" :series="wdBarSeries" />
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="chart-card">
                  <div class="chart-title">No-Show Rate pro Wochentag</div>
                  <apexchart type="bar" height="300" :options="wdNoShowOpts" :series="wdNoShowSeries" />
                </div>
              </v-col>
            </v-row>
            <v-data-table
              dark
              dense
              class="mt-4 transparent-table"
              :headers="wdHeaders"
              :items="weekdayItems"
              :items-per-page="7"
              hide-default-footer>
              <template v-slot:item.noShowRate="{ item }"
                ><v-chip x-small :color="noShowColor(item.noShowRate)" dark>{{ item.noShowRate }}%</v-chip></template
              >
            </v-data-table>
          </div>
        </v-tab-item>
        <!-- TAB 6: LÖSCHUNGEN -->
        <v-tab-item>
          <div class="pa-4">
            <v-row>
              <v-col cols="12" md="5">
                <div class="chart-card">
                  <div class="chart-title">Löschgründe</div>
                  <apexchart v-if="delDonutSeries.length" type="donut" height="320" :options="delDonutOpts" :series="delDonutSeries" />
                  <div v-else class="empty-state">Keine gelöschten Termine</div>
                </div>
              </v-col>
              <v-col cols="12" md="7">
                <div class="chart-card">
                  <div class="chart-title">Löschungen über Zeit</div>
                  <apexchart type="bar" height="320" :options="delTrendOpts" :series="delTrendSeries" />
                </div>
              </v-col>
            </v-row>
            <v-data-table
              v-if="data && data.deletionStats"
              dark
              dense
              class="mt-4 transparent-table"
              :headers="delHeaders"
              :items="data.deletionStats.byReason"
              :items-per-page="10"
              sort-by="count"
              sort-desc />
          </div>
        </v-tab-item>
        <!-- TAB 7: ROHDATEN -->
        <v-tab-item>
          <div class="pa-4">
            <div class="d-flex align-center mb-3" style="gap: 12px">
              <v-text-field v-model="search" label="Suchen" dark hide-details dense outlined append-icon="mdi-magnify" style="max-width: 360px" />
              <v-spacer />
              <v-btn small color="primary" @click="exportRaw"><v-icon left small>mdi-download</v-icon>Excel</v-btn>
            </div>
            <v-data-table
              dark
              dense
              class="transparent-table"
              :headers="rawHeaders"
              :items="filteredRaw"
              :items-per-page="20"
              sort-by="date"
              sort-desc>
              <template v-slot:item.status="{ item }"
                ><v-chip x-small :color="statusColor(item.status)" dark>{{ statusLabel(item.status) }}</v-chip></template
              >
            </v-data-table>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <v-overlay :value="loading" absolute opacity="0.4">
      <v-progress-circular indeterminate size="64" color="primary" />
    </v-overlay>
  </div>
</template>

<script>
import api from "../../../api"

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"]

export default {
  name: "WcDashboard",
  props: {
    studios: { type: Array, required: true },
    datePickerRange: { type: Array, required: true },
  },
  data() {
    return {
      loading: false,
      data: null,
      activeTab: 0,
      search: "",
      heatmapMetric: "appointments",
    }
  },
  async created() {
    await this.loadData()
  },
  watch: {
    studios() {
      this.loadData()
    },
    datePickerRange() {
      if (this.datePickerRange[0] && this.datePickerRange[1]) this.loadData()
    },
  },
  computed: {
    totals() {
      if (!this.data)
        return {
          totalAppointments: 0,
          totalTasks: 0,
          totalShifts: 0,
          totalEmployees: 0,
          avgAppointmentsPerDay: 0,
          taskCompletionRate: 0,
          noShowRate: 0,
          deletedCount: 0,
          statusDistribution: {},
          tasksDone: 0,
          tasksOpen: 0,
          completionsCount: 0,
        }
      return this.data.totals
    },
    // --- TAB 1: ÜBERSICHT ---
    trendSeries() {
      if (!this.data) return []
      const d = this.data.dailyStats
      return [
        { name: "Termine", data: d.map((x) => ({ x: x.date, y: x.appointments })) },
        { name: "Aufgaben", data: d.map((x) => ({ x: x.date, y: x.tasks })) },
      ]
    },
    trendOpts() {
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        stroke: { curve: "smooth", width: 2 },
        xaxis: { type: "datetime" },
        colors: ["#48c6ef", "#a18cd1"],
        fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0 } },
        dataLabels: { enabled: false },
        grid: { borderColor: "rgba(255,255,255,0.1)" },
        legend: { labels: { colors: "#fff" } },
      }
    },
    donutSeries() {
      if (!this.data) return []
      const s = this.totals.statusDistribution || {}
      return [s.planned || 0, s.checkedin || 0, s.done || 0, s.noshow || 0, s.cancelled || 0]
    },
    donutOpts() {
      return {
        chart: { foreColor: "#fff" },
        labels: ["Geplant", "Erschienen", "Erledigt", "No-Show", "Abgesagt"],
        colors: ["#6f86d6", "#48c6ef", "#43e97b", "#f5576c", "#ffa726"],
        legend: { position: "bottom", labels: { colors: "#fff" } },
        dataLabels: { style: { colors: ["#fff"] } },
        plotOptions: { pie: { donut: { size: "55%" } } },
      }
    },
    noShowTrendSeries() {
      if (!this.data) return []
      const d = this.data.dailyStats
      return [
        { name: "No-Shows", data: d.map((x) => ({ x: x.date, y: x.noShows })) },
        { name: "Absagen", data: d.map((x) => ({ x: x.date, y: x.cancelled })) },
      ]
    },
    noShowTrendOpts() {
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        stroke: { curve: "smooth", width: 3 },
        xaxis: { type: "datetime" },
        colors: ["#f5576c", "#ffa726"],
        dataLabels: { enabled: false },
        grid: { borderColor: "rgba(255,255,255,0.1)" },
        markers: { size: 3 },
        legend: { labels: { colors: "#fff" } },
      }
    },
    hourlyBarSeries() {
      if (!this.data) return []
      const hourMap = {}
      this.data.hourlyHeatmap.forEach((c) => {
        hourMap[c.hour] = (hourMap[c.hour] || 0) + c.appointments
      })
      const hours = Object.keys(hourMap).sort((a, b) => a - b)
      return [{ name: "Termine", data: hours.map((h) => hourMap[h]) }]
    },
    hourlyBarOpts() {
      if (!this.data) return {}
      const hourMap = {}
      this.data.hourlyHeatmap.forEach((c) => {
        hourMap[c.hour] = (hourMap[c.hour] || 0) + c.appointments
      })
      const hours = Object.keys(hourMap).sort((a, b) => a - b)
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        plotOptions: { bar: { borderRadius: 4, columnWidth: "60%" } },
        colors: ["#48c6ef"],
        dataLabels: { enabled: false },
        xaxis: { categories: hours.map((h) => h + ":00"), labels: { style: { colors: "#fff" } } },
        yaxis: { labels: { style: { colors: "#fff" } } },
        grid: { borderColor: "rgba(255,255,255,0.1)" },
      }
    },
    // --- TAB 2: MITARBEITER ---
    topEmployees() {
      if (!this.data) return []
      return [...this.data.employeeStats].sort((a, b) => b.appointments - a.appointments).slice(0, 5)
    },
    empBarSeries() {
      if (!this.data) return []
      const sorted = [...this.data.employeeStats].sort((a, b) => b.appointments - a.appointments).slice(0, 15)
      return [{ name: "Termine", data: sorted.map((e) => ({ x: e.name, y: e.appointments })) }]
    },
    empBarOpts() {
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
        colors: ["#48c6ef"],
        dataLabels: { enabled: true, style: { colors: ["#fff"] } },
        grid: { borderColor: "rgba(255,255,255,0.1)" },
        xaxis: { labels: { style: { colors: "#fff" } } },
        yaxis: { labels: { style: { colors: "#fff" } } },
      }
    },
    empHeaders() {
      return [
        { text: "Mitarbeiter", value: "name" },
        { text: "Termine", value: "appointments" },
        { text: "Tasks", value: "tasks" },
        { text: "Schichten", value: "shifts" },
        { text: "Arbeitsstd.", value: "totalWorkHours" },
        { text: "Task-Quote", value: "taskCompletionRate" },
        { text: "No-Show", value: "noShowRate" },
        { text: "Ø Termine/Schicht", value: "avgAppointmentsPerShift" },
      ]
    },
    // --- TAB 3: ÄMTLI ---
    tplBarSeries() {
      if (!this.data) return []
      const items = [...this.data.templateStats].slice(0, 10)
      return [{ name: "Erledigt %", data: items.map((t) => ({ x: t.title, y: t.completionRate })) }]
    },
    tplBarOpts() {
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
        colors: ["#43e97b"],
        dataLabels: { enabled: true, formatter: (v) => v + "%", style: { colors: ["#fff"] } },
        grid: { borderColor: "rgba(255,255,255,0.1)" },
        xaxis: { max: 100, labels: { style: { colors: "#fff" } } },
        yaxis: { labels: { style: { colors: "#fff" } } },
      }
    },
    taskTrendSeries() {
      if (!this.data) return []
      const d = this.data.dailyStats
      return [
        { name: "Tasks", data: d.map((x) => ({ x: x.date, y: x.tasks })) },
        { name: "Erledigt", data: d.map((x) => ({ x: x.date, y: x.tasksDone })) },
      ]
    },
    taskTrendOpts() {
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        stroke: { curve: "smooth", width: 2 },
        xaxis: { type: "datetime" },
        colors: ["#a18cd1", "#43e97b"],
        fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0 } },
        dataLabels: { enabled: false },
        grid: { borderColor: "rgba(255,255,255,0.1)" },
        legend: { labels: { colors: "#fff" } },
      }
    },
    tplHeaders() {
      return [
        { text: "Vorlage", value: "title" },
        { text: "Kategorie", value: "category" },
        { text: "Frequenz", value: "frequency" },
        { text: "Verwendet", value: "usageCount" },
        { text: "Erledigt", value: "doneCount" },
        { text: "Quote", value: "completionRate" },
        { text: "Ø / Tag", value: "avgPerDay" },
      ]
    },
    // --- TAB 4: HEATMAP ---
    hmSeries() {
      if (!this.data) return []
      const WDNAMES = ["Sonntag", "Samstag", "Freitag", "Donnerstag", "Mittwoch", "Dienstag", "Montag"]
      const grid = {}
      for (let w = 0; w < 7; w++) grid[w] = {}
      this.data.hourlyHeatmap.forEach((c) => {
        grid[c.weekday][c.hour] = (grid[c.weekday][c.hour] || 0) + c[this.heatmapMetric]
      })
      const series = []
      for (let w = 6; w >= 0; w--) {
        const row = { name: WDNAMES[6 - w], data: [] }
        for (let h = 6; h <= 22; h++) row.data.push({ x: h + ":00", y: grid[w][h] || 0 })
        series.push(row)
      }
      return series
    },
    hmOpts() {
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        dataLabels: { enabled: true, style: { colors: ["#fff"], fontSize: "10px" } },
        colors: ["#48c6ef"],
        xaxis: { labels: { style: { colors: "#fff" } } },
        plotOptions: {
          heatmap: {
            radius: 4,
            colorScale: {
              ranges: [
                { from: 0, to: 0, color: "#1a1a2e", name: "Keine" },
                { from: 1, to: 3, color: "#2d4a6b", name: "Wenig" },
                { from: 4, to: 8, color: "#4a7ba6", name: "Mittel" },
                { from: 9, to: 15, color: "#48c6ef", name: "Gut" },
                { from: 16, to: 1000, color: "#50fa7b", name: "Sehr gut" },
              ],
            },
          },
        },
      }
    },
    // --- TAB 5: WOCHENTAG ---
    weekdayItems() {
      if (!this.data) return []
      return this.data.weekdayStats.map((w) => ({ ...w, name: this.weekdayName(w.weekday) }))
    },
    wdBarSeries() {
      if (!this.data) return []
      return [{ name: "Ø Termine", data: this.data.weekdayStats.map((w) => w.avgAppointments) }]
    },
    wdBarOpts() {
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
        colors: ["#48c6ef"],
        dataLabels: { enabled: false },
        xaxis: { categories: this.data ? this.data.weekdayStats.map((w) => this.weekdayName(w.weekday)) : [], labels: { style: { colors: "#fff" } } },
        yaxis: { labels: { style: { colors: "#fff" } } },
        grid: { borderColor: "rgba(255,255,255,0.1)" },
      }
    },
    wdNoShowSeries() {
      if (!this.data) return []
      return [{ name: "No-Show %", data: this.data.weekdayStats.map((w) => w.noShowRate) }]
    },
    wdNoShowOpts() {
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
        colors: ["#f5576c"],
        dataLabels: { enabled: true, formatter: (v) => v + "%", style: { colors: ["#fff"] } },
        xaxis: { categories: this.data ? this.data.weekdayStats.map((w) => this.weekdayName(w.weekday)) : [], labels: { style: { colors: "#fff" } } },
        yaxis: { labels: { style: { colors: "#fff" } } },
        grid: { borderColor: "rgba(255,255,255,0.1)" },
      }
    },
    wdHeaders() {
      return [
        { text: "Tag", value: "name" },
        { text: "Termine", value: "appointments" },
        { text: "Ø Termine", value: "avgAppointments" },
        { text: "Tasks", value: "tasks" },
        { text: "No-Shows", value: "noShows" },
        { text: "No-Show Rate", value: "noShowRate" },
        { text: "Schichten", value: "shifts" },
      ]
    },
    // --- TAB 6: LÖSCHUNGEN ---
    delDonutSeries() {
      if (!this.data || !this.data.deletionStats) return []
      return this.data.deletionStats.byReason.map((r) => r.count)
    },
    delDonutOpts() {
      if (!this.data || !this.data.deletionStats) return {}
      return {
        chart: { foreColor: "#fff" },
        labels: this.data.deletionStats.byReason.map((r) => r.reason.substring(0, 30)),
        colors: ["#f5576c", "#ffa726", "#a18cd1", "#48c6ef", "#43e97b", "#667db6"],
        legend: { position: "bottom", labels: { colors: "#fff" } },
        dataLabels: { style: { colors: ["#fff"] } },
        plotOptions: { pie: { donut: { size: "55%" } } },
      }
    },
    delTrendSeries() {
      if (!this.data || !this.data.deletionStats) return []
      const byDay = this.data.deletionStats.byDay || {}
      const days = Object.keys(byDay).sort()
      return [{ name: "Löschungen", data: days.map((d) => ({ x: d, y: byDay[d] })) }]
    },
    delTrendOpts() {
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        plotOptions: { bar: { borderRadius: 3, columnWidth: "60%" } },
        colors: ["#f5576c"],
        dataLabels: { enabled: false },
        xaxis: { type: "datetime", labels: { style: { colors: "#fff" } } },
        yaxis: { labels: { style: { colors: "#fff" } } },
        grid: { borderColor: "rgba(255,255,255,0.1)" },
      }
    },
    delHeaders() {
      return [
        { text: "Grund", value: "reason" },
        { text: "Anzahl", value: "count" },
      ]
    },
    // --- TAB 7: ROHDATEN ---
    filteredRaw() {
      if (!this.data) return []
      if (!this.search) return this.data.rawAppointments
      const term = this.search.toLowerCase()
      return this.data.rawAppointments.filter((a) => `${a.title} ${a.employee} ${a.customer} ${a.date}`.toLowerCase().includes(term))
    },
    rawHeaders() {
      return [
        { text: "Datum", value: "date" },
        { text: "Start", value: "start" },
        { text: "Ende", value: "end" },
        { text: "Titel", value: "title" },
        { text: "Mitarbeiter", value: "employee" },
        { text: "Kunde", value: "customer" },
        { text: "Status", value: "status" },
      ]
    },
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        this.data = await api.shiftManagement.fetchWorkCalendarStatistics({
          studios: this.studios.map((s) => s._id),
          range: this.datePickerRange,
        })
      } catch (e) {
        this.$toast && this.$toast.error("Fehler beim Laden der Statistiken")
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    weekdayName(w) {
      return WEEKDAYS[w] || "?"
    },
    rateColor(v) {
      return v >= 80 ? "green" : v >= 50 ? "orange" : "red"
    },
    noShowColor(v) {
      return v <= 5 ? "green" : v <= 15 ? "orange" : "red"
    },
    statusColor(s) {
      return { planned: "blue-grey", checkedin: "cyan", done: "green", noshow: "red", cancelled: "orange" }[s] || "grey"
    },
    statusLabel(s) {
      return { planned: "Geplant", checkedin: "Erschienen", done: "Erledigt", noshow: "No-Show", cancelled: "Abgesagt" }[s] || s
    },
    exportRaw() {
      if (!this.data) return
      try {
        const XLSX = require("xlsx")
        const rows = this.filteredRaw.map((a) => ({
          Datum: a.date,
          Start: a.start,
          Ende: a.end,
          Titel: a.title,
          Mitarbeiter: a.employee,
          Kunde: a.customer,
          Status: this.statusLabel(a.status),
        }))
        const ws = XLSX.utils.json_to_sheet(rows)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, "Termine")
        XLSX.writeFile(wb, "WorkCalendar_Rohdaten.xlsx")
      } catch (e) {
        console.error(e)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.wc-dashboard {
  position: relative;
}

.kpi-card {
  border-radius: 16px;
  padding: 20px;
  color: #fff;
  height: 100%;
  backdrop-filter: blur(12px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  transition: transform 0.2s ease;
  &:hover {
    transform: translateY(-2px);
  }
  .kpi-label {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    opacity: 0.85;
  }
  .kpi-value {
    font-size: 2rem;
    font-weight: 700;
    margin-top: 4px;
    line-height: 1;
  }
  .kpi-sub {
    font-size: 0.75rem;
    opacity: 0.8;
    margin-top: 6px;
  }
}

.kpi-1 {
  background: linear-gradient(135deg, #48c6ef 0%, #6f86d6 100%);
}
.kpi-2 {
  background: linear-gradient(135deg, #f5576c 0%, #ff6b6b 100%);
}
.kpi-3 {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
  color: #1a1a2e;
}
.kpi-4 {
  background: linear-gradient(135deg, #667db6 0%, #0082c8 100%);
}
.kpi-5 {
  background: linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%);
  color: #1a1a2e;
}
.kpi-6 {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}
.kpi-7 {
  background: linear-gradient(135deg, #b7f8db 0%, #50a7c2 100%);
}
.kpi-8 {
  background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
  color: #1a1a2e;
}

.glass-card {
  background: rgba(255, 255, 255, 0.04) !important;
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px !important;
}

.chart-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 16px;
  height: 100%;
  .chart-title {
    color: #fff;
    font-weight: 600;
    font-size: 0.9rem;
    margin-bottom: 12px;
  }
}

.transparent-table {
  background: transparent !important;
  ::v-deep .v-data-table__wrapper {
    background: transparent;
  }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.95rem;
}

.top-slot-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  color: #fff;
  &:last-child {
    border-bottom: none;
  }
  .rank {
    font-size: 1.25rem;
    font-weight: 700;
    opacity: 0.6;
    min-width: 32px;
  }
  .slot-title {
    font-weight: 600;
    font-size: 0.9rem;
  }
  .slot-sub {
    font-size: 0.75rem;
    opacity: 0.65;
  }
  .slot-value {
    font-size: 1.25rem;
    font-weight: 700;
    color: #48c6ef;
  }
}
</style>
